import { Link } from "react-router-dom";
import PageHero from "../components/sections/PageHero";
import Process from "../components/sections/Process";
import ProjectShowcase from "../components/sections/ProjectShowcase";
import CTASection from "../components/sections/CTASection";
import ContactForm from "../components/sections/ContactForm";
import { EPOXY_TYPES } from "../data/content";
import { ArrowUpRight } from "lucide-react";

export default function EpoxyFlooring() {
  return (
    <>
      <PageHero
        eyebrow="Service 01 · Epoxy Flooring · Pan India"
        title="Premium Epoxy Flooring"
        italic="Across India."
        subtitle="Seamless, durable and professional flooring for garages, factories, warehouses, workshops, showrooms and commercial spaces — with epoxy services available Pan India."
        image="https://customer-assets-jai6qajn.emergentagent.net/job_painter-showcase-v1/artifacts/f8ktji6j_Screenshot%202026-08-16%20092114.png"
      />

      {/* Pan India banner */}
      <section
        data-testid="epoxy-pan-india-banner"
        data-on-dark
        className="bg-ink text-white py-10 md:py-14 border-b border-white/10"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/50 mb-3">
              Pan India Service
            </div>
            <h2 className="font-display text-2xl md:text-4xl leading-tight tracking-[-0.02em]">
              Epoxy flooring, delivered{" "}
              <span className="font-serif-italic font-normal text-white/75">
                anywhere in India.
              </span>
            </h2>
            <p className="mt-3 text-white/70 text-sm md:text-base max-w-2xl leading-relaxed">
              While our waterproofing and painting services are focused on
              Gurugram &amp; Delhi NCR, our epoxy flooring team travels for
              industrial, warehouse, factory and showroom projects across
              India. Share your city, area and timeline — we&rsquo;ll respond
              with a plan and quote.
            </p>
          </div>
          <ul className="md:col-span-5 grid grid-cols-2 gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-white/75">
            {[
              "Bangalore",
              "Hyderabad",
              "Kolkata",
              "Pune",
              "Chennai",
              "Baddi",
              "Ahmedabad",
              "Gurgaon",
            ].map((c) => (
              <li
                key={c}
                data-testid={`epoxy-city-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className="border border-white/15 px-3 py-2 flex items-center gap-2"
              >
                <span className="w-1 h-1 bg-white/60" /> {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Types grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-6">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink-muted mb-5">
                Epoxy Flooring Range
              </div>
              <h2 className="font-display text-3xl md:text-5xl text-ink leading-tight tracking-[-0.03em]">
                Systems for every{" "}
                <span className="font-serif-italic font-normal text-ink-mid">
                  space.
                </span>
              </h2>
            </div>
            <p className="md:col-span-6 self-end text-ink-muted leading-relaxed">
              From heavy-duty industrial floors to decorative residential
              coatings — every system requires proper surface preparation,
              accurate moisture assessment and the right build-up. That&rsquo;s
              where the finish really comes from.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-ink-line border border-ink-line">
            {EPOXY_TYPES.map((t, i) => (
              <div
                key={t}
                data-testid={`epoxy-type-${i}`}
                className="bg-white p-5 md:p-6 min-h-[130px] group hover:bg-ink hover:text-white transition-colors flex flex-col justify-between"
              >
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-muted group-hover:text-white/50">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display text-base md:text-lg mt-3 leading-tight">
                  {t}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Substrate note */}
      <section className="py-16 md:py-24 bg-ink-mist border-t border-ink-line">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink-muted mb-5">
              Substrate matters
            </div>
            <h3 className="font-display text-2xl md:text-4xl text-ink leading-tight tracking-[-0.02em]">
              An epoxy floor is only as good as the concrete under it.
            </h3>
          </div>
          <div className="md:col-span-7 text-ink-mid leading-relaxed space-y-4">
            <p>
              Every epoxy project starts with an assessment of the actual
              substrate condition — moisture content, cracks, contamination
              from oil or previous coatings, and the mechanical strength of the
              existing surface.
            </p>
            <p className="text-ink-muted">
              Surface preparation (grinding, shot blasting, crack repair,
              priming) is a critical part of the work. The chosen epoxy system
              is selected only after this inspection — heavy-duty, self-
              leveling, decorative, anti-slip and floor coating systems each
              have their own use case.
            </p>
          </div>
        </div>
      </section>

      <Process />
      <ProjectShowcase limit={8} />

      {/* Lead form */}
      <section className="py-16 md:py-24 bg-white border-t border-ink-line">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <ContactForm
            defaultService="Epoxy Flooring"
            leadType="quote"
            source="epoxy_page"
            heading="Get Epoxy Flooring Quote"
            subheading="Tell us about the site — area, current substrate, expected usage and any specific requirements."
          />
        </div>
      </section>

      <CTASection />

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10 flex flex-wrap gap-4 justify-between text-sm text-ink-muted">
        <Link to="/waterproofing" className="inline-flex items-center gap-2 hover:text-ink group">
          Next: Waterproofing <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
        </Link>
        <Link to="/painting" className="inline-flex items-center gap-2 hover:text-ink group">
          Or: Painting Services <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
        </Link>
      </div>
    </>
  );
}
